import React, { useState } from 'react';
import { collection, addDoc, Timestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useChildren } from '../context/ChildProvider';
import { Button } from '../components/UI';
import { evaluateGrowth } from '../helpers/growthEvaluation';
import { calculateAgeMonths } from '../helpers/utils';

export default function BatchMeasurementScreen({ user, navigate, showToast }) {
    const { sharedChildren, myChildren } = useChildren();
    const allPatients = [...sharedChildren, ...myChildren];
    
    // Get unique classes
    const classNames = [...new Set(allPatients.map(child => child.className).filter(Boolean))];
    const [selectedClass, setSelectedClass] = useState(classNames[0] || '');
    
    const childrenInClass = allPatients.filter(c => c.className === selectedClass);
    
    // State to hold measurements: { childId: { bb: '', tb: '', lk: '' } }
    const [measurements, setMeasurements] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    const handleInputChange = (childId, field, value) => {
        setMeasurements(prev => ({
            ...prev,
            [childId]: {
                ...prev[childId],
                [field]: value
            }
        }));
    };

    const handleSaveBatch = async () => {
        setIsSaving(true);
        let successCount = 0;
        
        try {
            for (const child of childrenInClass) {
                const data = measurements[child.id];
                if (!data || !data.bb || !data.tb || !data.lk) continue; // Skip incomplete

                const weight = parseFloat(data.bb);
                const height = parseFloat(data.tb);
                const headCirc = parseFloat(data.lk);
                const ageInMonths = calculateAgeMonths(child.dob.toDate());
                const gender = child.gender || 'Laki-laki'; 
                
                const growthEvaluation = evaluateGrowth(weight, height, headCirc, ageInMonths, gender);
                const status = growthEvaluation.overall.status;
                const createdAt = Timestamp.now();

                const assessmentData = {
                    pertumbuhan: {
                        bb: data.bb,
                        tb: data.tb,
                        lk: data.lk,
                        status: status,
                        interpretasi: `Status Keseluruhan: ${status}`,
                        rekomendasi: growthEvaluation.overall.recommendation,
                        evaluationData: growthEvaluation
                    },
                    overallStatus: status,
                    createdAt: createdAt,
                    childName: child.name,
                    childDob: child.dob,
                    ageInMonths: ageInMonths
                };

                const assessmentsPath = `children/${child.id}/assessments`;
                
                const docRef = await addDoc(collection(db, assessmentsPath), assessmentData);

                // Update last assessment
                const childDocRef = doc(db, `children/${child.id}`);
                await updateDoc(childDocRef, {
                    lastAssessment: {
                        id: docRef.id,
                        overallStatus: status,
                        createdAt: createdAt
                    }
                });
                
                successCount++;
            }
            
            showToast(`Berhasil menyimpan data untuk ${successCount} anak!`, 'success');
            setMeasurements({}); // Clear form
            navigate('home');
            
        } catch (error) {
            console.error("Gagal menyimpan batch:", error);
            showToast('Terjadi kesalahan saat menyimpan data.', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="bg-[#F8FAFC] min-h-screen pb-20">
            <div className="bg-emerald-600 text-white pt-10 pb-16 px-6 rounded-b-[2.5rem] shadow-lg">
                <h1 className="text-2xl font-extrabold mb-2">Input Massal Pengukuran</h1>
                <p className="text-emerald-100 text-sm">Masukkan Berat Badan (BB), Tinggi Badan (TB), dan Lingkar Kepala (LK) sekaligus.</p>
            </div>

            <div className="max-w-md mx-auto px-4 -mt-10 relative z-10">
                <div className="bg-white rounded-2xl p-5 shadow-xl border border-gray-100 mb-6">
                    <label className="block text-gray-700 font-bold mb-2 text-sm">Pilih Kelas</label>
                    <select 
                        value={selectedClass} 
                        onChange={e => setSelectedClass(e.target.value)} 
                        className="w-full bg-gray-50 border border-gray-200 text-gray-800 text-sm font-bold rounded-xl focus:ring-emerald-500 focus:border-emerald-500 block p-3 shadow-sm outline-none"
                    >
                        {classNames.length === 0 && <option value="">Tidak ada kelas</option>}
                        {classNames.map(name => <option key={name} value={name}>{name}</option>)}
                    </select>
                </div>

                {childrenInClass.length > 0 ? (
                    <div className="space-y-4">
                        {childrenInClass.map(child => (
                            <div key={child.id} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                                <h3 className="font-bold text-gray-800 mb-3 flex justify-between items-center">
                                    <span>{child.name}</span>
                                    <span className="text-xs text-gray-500 font-normal">{calculateAgeMonths(child.dob.toDate())} bln</span>
                                </h3>
                                <div className="grid grid-cols-3 gap-3">
                                    <div>
                                        <label className="text-xs font-bold text-gray-500 mb-1 block">BB (kg)</label>
                                        <input 
                                            type="number" step="0.1" 
                                            value={measurements[child.id]?.bb || ''} 
                                            onChange={e => handleInputChange(child.id, 'bb', e.target.value)}
                                            className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold text-gray-500 mb-1 block">TB (cm)</label>
                                        <input 
                                            type="number" step="0.1" 
                                            value={measurements[child.id]?.tb || ''} 
                                            onChange={e => handleInputChange(child.id, 'tb', e.target.value)}
                                            className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold text-gray-500 mb-1 block">LK (cm)</label>
                                        <input 
                                            type="number" step="0.1" 
                                            value={measurements[child.id]?.lk || ''} 
                                            onChange={e => handleInputChange(child.id, 'lk', e.target.value)}
                                            className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}

                        <Button onClick={handleSaveBatch} disabled={isSaving} className="w-full mt-6 shadow-xl bg-emerald-600 hover:bg-emerald-700 py-4 text-base rounded-2xl">
                            {isSaving ? 'Menyimpan...' : 'Simpan Semua Pengukuran'}
                        </Button>
                    </div>
                ) : (
                    <div className="text-center py-10">
                        <p className="text-gray-500">Pilih kelas yang memiliki murid untuk mulai menginput.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
